// A synthesised alarm (Web Audio) so the alert needs no audio file to ship.
//
// Browsers keep an AudioContext suspended until the page has had a user
// gesture, so the first click / key press anywhere in the app unlocks it; an
// alert that arrives before that is silent, but still shows.

let ctx: AudioContext | null = null;
let unlockInstalled = false;

const getContext = (): AudioContext | null => {
  if (ctx) return ctx;
  const Ctor =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext })
      .webkitAudioContext;
  if (!Ctor) return null;
  try {
    ctx = new Ctor();
  } catch {
    ctx = null;
  }
  return ctx;
};

/** Call once at start-up: the first user gesture wakes the audio context. */
export const installAudioUnlock = () => {
  if (unlockInstalled) return;
  unlockInstalled = true;
  const unlock = () => {
    const c = getContext();
    if (c && c.state === "suspended") void c.resume();
    window.removeEventListener("pointerdown", unlock);
    window.removeEventListener("keydown", unlock);
  };
  window.addEventListener("pointerdown", unlock);
  window.addEventListener("keydown", unlock);
};

const tone = (c: AudioContext, freq: number, start: number, length: number) => {
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = "square";
  osc.frequency.value = freq;
  gain.gain.setValueAtTime(0.0001, start);
  gain.gain.exponentialRampToValueAtTime(0.25, start + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.0001, start + length);
  osc.connect(gain);
  gain.connect(c.destination);
  osc.start(start);
  osc.stop(start + length + 0.02);
};

/** One "ding-dong-ding-dong" burst, about a second long. */
const playBurst = () => {
  const c = getContext();
  if (!c) return;
  if (c.state === "suspended") void c.resume();
  if (c.state !== "running") return;
  const t = c.currentTime;
  tone(c, 988, t, 0.22);
  tone(c, 740, t + 0.25, 0.22);
  tone(c, 988, t + 0.5, 0.22);
  tone(c, 740, t + 0.75, 0.22);
};

/** Repeats the alarm until the returned function is called. */
export const startAlarm = (): (() => void) => {
  playBurst();
  const timer = window.setInterval(playBurst, 2200);
  return () => window.clearInterval(timer);
};
